import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { AllTerminalsQuery, TerminalNode } from '@/modules/terminal'
import { ALL_TERMINALS } from '@/modules/terminal'
import React from 'react'
import { useQuery } from '@apollo/client'
import { isMobileOnly } from 'react-device-detect'
import styled from 'styled-components'
import { useConfirm, useOnErrorAuto } from '@/utils/hooks'
import type { TerminalForm } from '../components'
import {
  OldAddButton,
  CreateTerminalPrompt,
  InfoBox,
  OldSection,
  SectionHeader,
  SettingsWrapper,
  TerminalSettingsCards,
  TerminalSettingsTable,
} from '../components'
import { useTerminalMutations } from '../util/mutations.hooks'

export const LS_KEY_LIST_VIEW_MODE_TERMINAL_SETTINGS =
  'firmadok-list-view-mode-terminal-settings'

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

interface TerminalSettingsProps {}

export const TerminalSettings: React.FC<TerminalSettingsProps> = () => {
  const translations = useTranslate({
    info1: 'settings.info.terminal-1',
    info2: 'settings.info.terminal-2',

    terminalNetworks: 'terminal.terminal-areas',

    prompt: {
      delete: ['settings.prompts.terminal-delete', { name: '' }],
      deleteTitle: 'settings.prompts.terminal-delete-title',
    },
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const onErrorAuto = useOnErrorAuto()

  const { data: queryData, loading: queryLoading } =
    useQuery<AllTerminalsQuery>(ALL_TERMINALS, {
      onError: onErrorAuto(),
    })

  const terminals = queryData?.allTerminals.edges.map(edge => edge.node) ?? []

  const mutations = useTerminalMutations()

  async function handleAdd() {
    const { data } = await addPrompt<TerminalForm | null>(resolve => (
      <CreateTerminalPrompt onSubmit={resolve} />
    ))
    if (data === null) return

    void mutations.create({
      variables: {
        input: {
          name: data.name,
          ip: data.ip,
          ipFilterRange: data.range,
        },
      },
    })
  }
  async function handleEdit(terminal: TerminalNode) {
    const initialData: TerminalForm = {
      name: terminal.name,
      ip: terminal.ip,
      range: terminal.ipFilterRange,
    }
    const { data } = await addPrompt<TerminalForm | null>(resolve => (
      <CreateTerminalPrompt initialData={initialData} onSubmit={resolve} />
    ))
    if (data === null) return

    void mutations.patch({
      variables: {
        id: terminal.id,
        input: {
          name: data.name,
          ip: data.ip,
          ipFilterRange: data.range,
        },
      },
    })
  }
  async function handleDelete(terminal: TerminalNode) {
    const { data: answer } = await confirm(
      translations.prompt.delete({ name: terminal.name }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    void mutations.delete({
      variables: {
        id: terminal.id,
      },
    })
  }

  const isLoading = queryLoading || mutations.loading

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <InfoBox initCollapsed={isMobileOnly}>
        <p>{translations.info1}</p>
        <p>{translations.info2}</p>
      </InfoBox>

      <OldSection>
        <Header>
          <SectionHeader loading={isLoading}>
            {translations.terminalNetworks}
          </SectionHeader>

          {!isMobileOnly && <OldAddButton adding={false} onClick={handleAdd} />}
        </Header>

        {isMobileOnly ? (
          <TerminalSettingsCards
            terminals={terminals}
            onAdd={handleAdd}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ) : (
          <TerminalSettingsTable
            terminals={terminals}
            onAdd={handleAdd}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}
      </OldSection>
    </SettingsWrapper>
  )
}
